import { useCallback, useId } from "react";
import {
    components,
    FormatOptionLabelMeta,
    InputProps,
    OptionProps
} from "react-select";

import { AsyncSelect } from "@/components/Form/Base/asyncSelect.component";
import { SearchItemComponent } from "@/components/Store/StoreSearchInput/components/searchItem/searchItem.component";
import { useStoreSearchInputContext } from "@/components/Store/StoreSearchInput/context/storeSearchInput.context";
import { SearchItem } from "@/components/Store/StoreSearchInput/storeSearchInput.types";
import { useGranite } from "@/redux/modules/value/translations/translations.provider";

type Props = {
    className?: string;
};

export const Input = ({ className }: Props) => {
    const { t } = useGranite();

    const {
        disabled,
        focussed,
        open,
        loading,
        clearable,
        asyncRef,
        focusRef,
        menuPortalTarget,
        value,
        placeholder,
        loadingText,
        "aria-label": ariaLabel,
        loadOptions,
        onChange,
        onBlur,
        onFocus,
        onInputChange
    } = useStoreSearchInputContext();

    const Option = useCallback(
        (props: OptionProps<SearchItem>) => {
            const { isFocused, data } = props;
            if (isFocused) focusRef.current = data;

            return <components.Option {...props} />;
        },
        [focusRef]
    );

    const OptionLabel = useCallback(
        (
            data: SearchItem,
            formatOptionLabelMeta: FormatOptionLabelMeta<SearchItem>
        ) => {
            return (
                <SearchItemComponent
                    value={data.value}
                    label={data.highlightedLabel ?? data.label}
                    type={data.type}
                    highlight={formatOptionLabelMeta.context === "menu"}
                />
            );
        },
        []
    );

    const Input = useCallback((props: InputProps<SearchItem>) => {
        return (
            <components.Input
                {...props}
                inputClassName={"store-search-input__input"}
            />
        );
    }, []);

    const id = useId();

    return (
        <AsyncSelect
            isDisabled={disabled}
            ref={asyncRef}
            instanceId={id}
            id={"store-search-input"}
            menuPortalTarget={menuPortalTarget}
            menuIsOpen={open}
            isClearable={clearable}
            cacheOptions={false}
            isLoading={loading}
            className={className}
            components={{
                Option,
                Input,
                LoadingIndicator: () => null,
                DropdownIndicator: () => null,
                IndicatorSeparator: () => null
            }}
            classNames={{
                control: () =>
                    "tw:border-none! tw:shadow-none! tw:bg-transparent! tw:gap-2!",
                menu: () => "tw:overflow-hidden",
                menuList: () => "tw:overflow-hidden tw:pt-0! tw:pb-0!",
                placeholder: () => "tw:whitespace-nowrap tw:overflow-hidden"
            }}
            openMenuOnClick={false}
            openMenuOnFocus={false}
            defaultOptions={false}
            isMulti={false}
            blurInputOnSelect={true}
            loadOptions={loadOptions}
            defaultValue={value}
            value={focussed ? undefined : value}
            formatOptionLabel={OptionLabel}
            placeholder={placeholder}
            loadingMessage={() => loadingText}
            noOptionsMessage={() => t("general.selectBox.no.options")}
            aria-label={ariaLabel}
            onChange={onChange}
            onFocus={onFocus}
            onBlur={onBlur}
            onInputChange={onInputChange}
        />
    );
};
